import {
    RosterStatus,
    SearchFacetInput,
    SearchIndexField,
    SortField,
    SortIndexField,
} from '__generated__/graphql'
import { useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { decodeFilters, encodeFilters } from './filterCodecs'
import { decodeSort, encodeSort } from './sortCodecs'
import {
    DEFAULT_LIMIT,
    parseStringArray,
    parsePageFromUrl,
    parseLimitFromUrl,
    serializeStringArray,
} from './urlParsingUtils'

export interface RosterFilterState {
    status: string[]
    type: string | undefined
    name: string | undefined
    quickSearch: string | undefined
    company: string[]
    createdBy: string[]
    createdAtGte: string | undefined
    createdAtLte: string | undefined
    internalUsers: string[]
    industries: string[]
    sectors: string[]
    geoLocations: string[]
    geoLocationText: string[]
    startDate: string[]
    processStatus: string[]
    titles: string[]
}

export interface RosterSortState {
    field: SortIndexField
    order: 'asc' | 'desc'
}

const DEFAULT_STATUSES = [
    RosterStatus.Active,
    RosterStatus.Opportunistic,
    RosterStatus.OnHold,
]

const getDefaultFilters = (): RosterFilterState => ({
    status: DEFAULT_STATUSES,
    type: undefined,
    name: undefined,
    quickSearch: undefined,
    company: [],
    createdBy: [],
    createdAtGte: undefined,
    createdAtLte: undefined,
    internalUsers: [],
    industries: [],
    sectors: [],
    geoLocations: [],
    geoLocationText: [],
    startDate: [],
    processStatus: [],
    titles: [],
})

const parseFiltersFromUrl = (
    searchParams: URLSearchParams
): RosterFilterState => ({
    status: parseStringArray(searchParams.get('status')),
    type: searchParams.get('type') || undefined,
    name: searchParams.get('name') || undefined,
    quickSearch: searchParams.get('quickSearch') || undefined,
    company: parseStringArray(searchParams.get('company')),
    createdBy: parseStringArray(searchParams.get('createdBy')),
    createdAtGte: searchParams.get('createdAtGte') || undefined,
    createdAtLte: searchParams.get('createdAtLte') || undefined,
    internalUsers: parseStringArray(searchParams.get('internalUsers')),
    industries: parseStringArray(searchParams.get('industries')),
    sectors: parseStringArray(searchParams.get('sectors')),
    geoLocations: parseStringArray(searchParams.get('geoLocations')),
    geoLocationText: parseStringArray(searchParams.get('geoLocationText')),
    startDate: parseStringArray(searchParams.get('startDate')),
    processStatus: parseStringArray(searchParams.get('processStatus')),
    titles: parseStringArray(searchParams.get('titles')),
})

const parseSortFromUrl = (searchParams: URLSearchParams): RosterSortState[] => {
    const sortField = searchParams.get('sortField')
    const sortOrder = searchParams.get('sortOrder')

    if (!sortField || !sortOrder) return []

    const validFields = Object.values(SortIndexField)
    if (!validFields.includes(sortField as SortIndexField)) return []

    if (sortOrder !== 'asc' && sortOrder !== 'desc') return []

    return [{ field: sortField as SortIndexField, order: sortOrder }]
}

const buildFiltersToUrl = (
    filters: RosterFilterState,
    params: URLSearchParams
): void => {
    if (filters.status.length > 0)
        params.set('status', serializeStringArray(filters.status))
    if (filters.type) params.set('type', filters.type)
    if (filters.name) params.set('name', filters.name)
    if (filters.quickSearch) params.set('quickSearch', filters.quickSearch)
    if (filters.company.length > 0)
        params.set('company', serializeStringArray(filters.company))
    if (filters.createdBy.length > 0)
        params.set('createdBy', serializeStringArray(filters.createdBy))
    if (filters.createdAtGte) params.set('createdAtGte', filters.createdAtGte)
    if (filters.createdAtLte) params.set('createdAtLte', filters.createdAtLte)
    if (filters.internalUsers.length > 0)
        params.set('internalUsers', serializeStringArray(filters.internalUsers))
    if (filters.industries.length > 0)
        params.set('industries', serializeStringArray(filters.industries))
    if (filters.sectors.length > 0)
        params.set('sectors', serializeStringArray(filters.sectors))
    if (filters.geoLocations.length > 0)
        params.set('geoLocations', serializeStringArray(filters.geoLocations))
    if (filters.geoLocationText.length > 0)
        params.set(
            'geoLocationText',
            serializeStringArray(filters.geoLocationText)
        )
    if (filters.startDate.length > 0)
        params.set('startDate', serializeStringArray(filters.startDate))
    if (filters.processStatus.length > 0)
        params.set('processStatus', serializeStringArray(filters.processStatus))
    if (filters.titles.length > 0)
        params.set('titles', serializeStringArray(filters.titles))
}

const buildSortToUrl = (
    sort: RosterSortState[],
    params: URLSearchParams
): void => {
    if (sort.length > 0) {
        params.set('sortField', sort[0].field)
        params.set('sortOrder', sort[0].order)
    }
}

const filtersToGraphQL = (filters: RosterFilterState): SearchFacetInput[] => {
    const facets: SearchFacetInput[] = []

    if (filters.status.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterStatus,
            value: { stringArray: filters.status },
        })
    }
    if (filters.type) {
        facets.push({
            facet: SearchIndexField.RosterType,
            value: { string: filters.type },
        })
    }
    if (filters.name) {
        facets.push({
            facet: SearchIndexField.RosterName,
            value: { string: filters.name },
        })
    }
    if (filters.quickSearch) {
        facets.push({
            facet: SearchIndexField.RosterQuickSearch,
            value: { string: filters.quickSearch },
        })
    }
    if (filters.company.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterCompany,
            value: { stringArray: filters.company },
        })
    }
    if (filters.createdBy.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterCreatedBy,
            value: { stringArray: filters.createdBy },
        })
    }
    if (filters.createdAtGte || filters.createdAtLte) {
        facets.push({
            facet: SearchIndexField.RosterCreatedAt,
            value: {
                dateRange: {
                    gte: filters.createdAtGte || null,
                    lte: filters.createdAtLte || null,
                },
            },
        })
    }
    if (filters.internalUsers.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterInternalUsers,
            value: { stringArray: filters.internalUsers },
        })
    }
    if (filters.industries.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterIndustries,
            value: { stringArray: filters.industries },
        })
    }
    if (filters.sectors.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterSectors,
            value: { stringArray: filters.sectors },
        })
    }
    if (filters.geoLocations.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterGeoLocations,
            value: { stringArray: filters.geoLocations },
        })
    }
    if (filters.geoLocationText.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterGeoLocationText,
            value: { stringArray: filters.geoLocationText },
        })
    }
    if (filters.startDate.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterStartDate,
            value: { stringArray: filters.startDate },
        })
    }
    if (filters.processStatus.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterCandidateProcessStatus,
            value: { stringArray: filters.processStatus },
        })
    }
    if (filters.titles.length > 0) {
        facets.push({
            facet: SearchIndexField.RosterTitles,
            value: { stringArray: filters.titles },
        })
    }

    return facets
}

const sortToGraphQL = (sort: RosterSortState[]): SortField[] =>
    sort.map((s) => ({ field: s.field, order: s.order }))

const graphQLFiltersToState = (
    facets: SearchFacetInput[]
): RosterFilterState => {
    const state = getDefaultFilters()

    for (const facet of facets) {
        switch (facet.facet) {
            case SearchIndexField.RosterStatus:
                state.status = facet.value.stringArray || []
                break
            case SearchIndexField.RosterType:
                state.type = facet.value.string || undefined
                break
            case SearchIndexField.RosterName:
                state.name = facet.value.string || undefined
                break
            case SearchIndexField.RosterQuickSearch:
                state.quickSearch = facet.value.string || undefined
                break
            case SearchIndexField.RosterCompany:
                state.company = facet.value.stringArray || []
                break
            case SearchIndexField.RosterCreatedBy:
                state.createdBy = facet.value.stringArray || []
                break
            case SearchIndexField.RosterCreatedAt:
                state.createdAtGte = facet.value.dateRange?.gte || undefined
                state.createdAtLte = facet.value.dateRange?.lte || undefined
                break
            case SearchIndexField.RosterInternalUsers:
                state.internalUsers = facet.value.stringArray || []
                break
            case SearchIndexField.RosterIndustries:
                state.industries = facet.value.stringArray || []
                break
            case SearchIndexField.RosterSectors:
                state.sectors = facet.value.stringArray || []
                break
            case SearchIndexField.RosterGeoLocations:
                state.geoLocations = facet.value.stringArray || []
                break
            case SearchIndexField.RosterGeoLocationText:
                state.geoLocationText = facet.value.stringArray || []
                break
            case SearchIndexField.RosterStartDate:
                state.startDate = facet.value.stringArray || []
                break
            case SearchIndexField.RosterCandidateProcessStatus:
                state.processStatus = facet.value.stringArray || []
                break
            case SearchIndexField.RosterTitles:
                state.titles = facet.value.stringArray || []
                break
        }
    }

    return state
}

const graphQLSortToState = (sort: SortField[]): RosterSortState[] =>
    sort.map((s) => ({ field: s.field, order: s.order as 'asc' | 'desc' }))

const hasActiveFilters = (state: RosterFilterState): boolean => {
    const defaults = getDefaultFilters()
    return (
        state.status.length !== defaults.status.length ||
        state.type !== defaults.type ||
        state.name !== defaults.name ||
        state.quickSearch !== defaults.quickSearch ||
        state.company.length !== defaults.company.length ||
        state.createdBy.length !== defaults.createdBy.length ||
        state.createdAtGte !== defaults.createdAtGte ||
        state.createdAtLte !== defaults.createdAtLte ||
        state.internalUsers.length !== defaults.internalUsers.length ||
        state.industries.length !== defaults.industries.length ||
        state.sectors.length !== defaults.sectors.length ||
        state.geoLocations.length !== defaults.geoLocations.length ||
        state.geoLocationText.length !== defaults.geoLocationText.length ||
        state.startDate.length !== defaults.startDate.length ||
        state.processStatus.length !== defaults.processStatus.length ||
        state.titles.length !== defaults.titles.length
    )
}

export function useRosterSearchUrlState() {
    const [searchParams, setSearchParams] = useSearchParams()
    const migratedRef = useRef(false)

    useEffect(() => {
        if (migratedRef.current) return

        const oldFilters = searchParams.get('filters')
        const oldSort = searchParams.get('sort')

        if (
            (oldFilters && oldFilters.includes('~')) ||
            (oldSort && oldSort.includes('~'))
        ) {
            migratedRef.current = true
            const decodedFilters = oldFilters ? decodeFilters(oldFilters) : []
            const decodedSort = oldSort ? decodeSort(oldSort) : []

            const migratedFilterState = graphQLFiltersToState(decodedFilters)
            const migratedSortState = graphQLSortToState(decodedSort)

            const newParams = new URLSearchParams()
            const page = searchParams.get('page')
            const limit = searchParams.get('limit')
            if (page) newParams.set('page', page)
            if (limit) newParams.set('limit', limit)
            buildFiltersToUrl(migratedFilterState, newParams)
            buildSortToUrl(migratedSortState, newParams)

            setSearchParams(newParams, { replace: true })
        }
    }, [searchParams, setSearchParams])

    const page = parsePageFromUrl(searchParams)
    const limit = parseLimitFromUrl(searchParams)

    const filters = parseFiltersFromUrl(searchParams)
    const sort = parseSortFromUrl(searchParams)

    const graphQLFilters = filtersToGraphQL(filters)
    const graphQLSort = sortToGraphQL(sort)

    const setPage = useCallback(
        (newPage: number) => {
            setSearchParams(
                (prev) => {
                    prev.set('page', String(newPage))
                    return prev
                },
                { replace: false }
            )
        },
        [setSearchParams]
    )

    const setLimit = useCallback(
        (newLimit: number) => {
            setSearchParams(
                (prev) => {
                    prev.set('limit', String(newLimit))
                    prev.set('page', '1')
                    return prev
                },
                { replace: false }
            )
        },
        [setSearchParams]
    )

    const setFilters = useCallback(
        (newFilters: RosterFilterState) => {
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams()
                    newParams.set('page', '1')
                    newParams.set(
                        'limit',
                        prev.get('limit') || String(DEFAULT_LIMIT)
                    )
                    buildFiltersToUrl(newFilters, newParams)
                    const sortField = prev.get('sortField')
                    const sortOrder = prev.get('sortOrder')
                    if (sortField && sortOrder) {
                        newParams.set('sortField', sortField)
                        newParams.set('sortOrder', sortOrder)
                    }
                    return newParams
                },
                { replace: false }
            )
        },
        [setSearchParams]
    )

    const clearFilters = useCallback(() => {
        setSearchParams(
            (prev) => {
                const newParams = new URLSearchParams()
                newParams.set('page', '1')
                newParams.set(
                    'limit',
                    prev.get('limit') || String(DEFAULT_LIMIT)
                )
                const sortField = prev.get('sortField')
                const sortOrder = prev.get('sortOrder')
                if (sortField && sortOrder) {
                    newParams.set('sortField', sortField)
                    newParams.set('sortOrder', sortOrder)
                }
                return newParams
            },
            { replace: false }
        )
    }, [setSearchParams])

    const setSort = useCallback(
        (newSort: RosterSortState[]) => {
            setSearchParams(
                (prev) => {
                    prev.set('page', '1')
                    prev.delete('sortField')
                    prev.delete('sortOrder')
                    buildSortToUrl(newSort, prev)
                    return prev
                },
                { replace: false }
            )
        },
        [setSearchParams]
    )

    const setGraphQLFilters = useCallback(
        (facets: SearchFacetInput[]) => {
            setFilters(graphQLFiltersToState(facets))
        },
        [setFilters]
    )

    const setGraphQLSort = useCallback(
        (sortFields: SortField[]) => {
            setSort(graphQLSortToState(sortFields))
        },
        [setSort]
    )

    return {
        page,
        limit,
        filters,
        sort,
        graphQLFilters,
        graphQLSort,
        hasActiveFilters: hasActiveFilters(filters),
        setPage,
        setLimit,
        setFilters,
        clearFilters,
        setSort,
        setGraphQLFilters,
        setGraphQLSort,
    }
}

export { encodeFilters, decodeFilters, encodeSort, decodeSort }
